// PERFORMANCE OPTIMIZATION ADDED - React.memo and useMemo for better performance
import React, { memo, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Leaf,
  Phone,
  Mail,
  MapPin,
  Facebook,
  Instagram,
  Twitter,
} from "lucide-react";
import { farmInfo } from "../data/farmInfo";

const Footer: React.FC = memo(() => {
  // PERFORMANCE OPTIMIZATION ADDED - useMemo to memoize currentYear
  const currentYear = useMemo(() => new Date().getFullYear(), []);

  return (
    <footer className="relative overflow-hidden bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950 text-white">
      {/* UI/UX REFRESH - Decorative hairline and soft brand glow */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-mango-500/70 to-transparent" aria-hidden="true" />
      <div className="pointer-events-none absolute -top-40 left-1/2 h-80 w-[48rem] max-w-none -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(249,115,22,0.16),transparent)]" aria-hidden="true" />
      <div className="container-custom section-padding relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Farm Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-1 flex flex-col"
          >
            <div className="flex items-center space-x-2 mb-4 h-6">
              <Leaf className="h-5 w-5 text-mango-400 flex-shrink-0" aria-hidden="true" />
              <span className="text-base font-bold text-mango-400 font-logo tracking-wide whitespace-nowrap" style={{ letterSpacing: '0.05em' }}>
                Golden Harvest Mango
              </span>
            </div>
            <p className="text-sm text-gray-300 mb-4 leading-relaxed">
              {farmInfo.description}
            </p>
            <div className="flex space-x-3 mt-auto" role="list" aria-label="Social media links">
              {farmInfo.socialMedia.facebook && (
                <a
                  href={farmInfo.socialMedia.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-gradient-to-br from-mango-500 to-mango-700 rounded-full ring-1 ring-white/10 hover:-translate-y-1 hover:shadow-glow hover:from-mango-400 hover:to-mango-600 transition duration-300"
                  aria-label="Visit our Facebook page"
                >
                  <Facebook className="h-4 w-4" aria-hidden="true" />
                </a>
              )}
              {farmInfo.socialMedia.instagram && (
                <a
                  href={farmInfo.socialMedia.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-gradient-to-br from-mango-500 to-mango-700 rounded-full ring-1 ring-white/10 hover:-translate-y-1 hover:shadow-glow hover:from-mango-400 hover:to-mango-600 transition duration-300"
                  aria-label="Visit our Instagram page"
                >
                  <Instagram className="h-4 w-4" aria-hidden="true" />
                </a>
              )}
              {farmInfo.socialMedia.twitter && (
                <a
                  href={farmInfo.socialMedia.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-gradient-to-br from-mango-500 to-mango-700 rounded-full ring-1 ring-white/10 hover:-translate-y-1 hover:shadow-glow hover:from-mango-400 hover:to-mango-600 transition duration-300"
                  aria-label="Visit our Twitter page"
                >
                  <Twitter className="h-4 w-4" aria-hidden="true" />
                </a>
              )}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-1 flex flex-col"
          >
            <h3 className="text-base font-semibold mb-4 text-mango-400 uppercase tracking-wide h-6">
              Quick Links
            </h3>
            <ul className="space-y-2.5">
              <li>
                <Link
                  to="/"
                  className="inline-block text-sm text-gray-300 hover:text-mango-300 hover:translate-x-1 transition duration-300"
                  aria-label="Navigate to home page"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className="inline-block text-sm text-gray-300 hover:text-mango-300 hover:translate-x-1 transition duration-300"
                >
                  About Our Farm
                </Link>
              </li>
              <li>
                <Link
                  to="/mangoes"
                  className="inline-block text-sm text-gray-300 hover:text-mango-300 hover:translate-x-1 transition duration-300"
                >
                  Buy Mangoes Online
                </Link>
              </li>
              <li>
                <Link
                  to="/gallery"
                  className="inline-block text-sm text-gray-300 hover:text-mango-300 hover:translate-x-1 transition duration-300"
                >
                  Gallery
                </Link>
              </li>
              <li>
                <Link
                  to="/blog"
                  className="inline-block text-sm text-gray-300 hover:text-mango-300 hover:translate-x-1 transition duration-300"
                >
                  Mango Blog & Guides
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="inline-block text-sm text-gray-300 hover:text-mango-300 hover:translate-x-1 transition duration-300"
                >
                  Contact Us
                </Link>
              </li>
            </ul>
          </motion.div>

          {/* Delivery Locations */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-1 flex flex-col"
          >
            <h3 className="text-base font-semibold mb-4 text-mango-400 uppercase tracking-wide h-6">
              Delivery Locations
            </h3>
            <ul className="space-y-2.5">
              <li>
                <Link
                  to="/mangoes-delivery-delhi"
                  className="inline-block text-sm text-gray-300 hover:text-mango-300 hover:translate-x-1 transition duration-300"
                >
                  Mangoes Delivery Delhi
                </Link>
              </li>
              <li>
                <Link
                  to="/mangoes-delivery-meerut"
                  className="inline-block text-sm text-gray-300 hover:text-mango-300 hover:translate-x-1 transition duration-300"
                >
                  Mangoes Delivery Meerut
                </Link>
              </li>
              <li>
                <Link
                  to="/mangoes-delivery-chandigarh"
                  className="inline-block text-sm text-gray-300 hover:text-mango-300 hover:translate-x-1 transition duration-300"
                >
                  Mangoes Delivery Chandigarh
                </Link>
              </li>
              <li>
                <Link
                  to="/mangoes-delivery-ambala"
                  className="inline-block text-sm text-gray-300 hover:text-mango-300 hover:translate-x-1 transition duration-300"
                >
                  Mangoes Delivery Ambala
                </Link>
              </li>
              <li>
                <Link
                  to="/shipping"
                  className="inline-block text-sm text-gray-300 hover:text-mango-300 hover:translate-x-1 transition duration-300"
                >
                  View All Locations
                </Link>
              </li>
            </ul>
          </motion.div>

          {/* Legal Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-1 flex flex-col"
          >
            <h3 className="text-base font-semibold mb-4 text-mango-400 uppercase tracking-wide h-6">
              Legal
            </h3>
            <ul className="space-y-2.5">
              <li>
                <Link
                  to="/terms-and-conditions"
                  className="inline-block text-sm text-gray-300 hover:text-mango-300 hover:translate-x-1 transition duration-300"
                  aria-label="View terms and conditions"
                >
                  Terms and Conditions
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  className="inline-block text-sm text-gray-300 hover:text-mango-300 hover:translate-x-1 transition duration-300"
                  aria-label="View privacy policy"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/shipping"
                  className="inline-block text-sm text-gray-300 hover:text-mango-300 hover:translate-x-1 transition duration-300"
                  aria-label="View shipping policy"
                >
                  Shipping Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/cancellation-refunds"
                  className="inline-block text-sm text-gray-300 hover:text-mango-300 hover:translate-x-1 transition duration-300"
                  aria-label="View cancellation and refunds policy"
                >
                  Cancellation & Refunds
                </Link>
              </li>
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:col-span-1 flex flex-col"
          >
            <h3 className="text-base font-semibold mb-4 text-mango-400 uppercase tracking-wide h-6">
              Contact Info
            </h3>
            <div className="space-y-2.5">
              <div className="flex items-start space-x-2.5">
                <Phone className="h-4 w-4 text-mango-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <a
                  href={`tel:${farmInfo.contact.phone}`}
                  className="inline-block text-sm text-gray-300 hover:text-mango-300 hover:translate-x-1 transition duration-300"
                  aria-label={`Call us at ${farmInfo.contact.phone}`}
                >
                  {farmInfo.contact.phone}
                </a>
              </div>
              {farmInfo.contact.phone2 && (
                <div className="flex items-start space-x-2.5">
                  <Phone className="h-4 w-4 text-mango-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <a
                    href={`tel:${farmInfo.contact.phone2}`}
                    className="inline-block text-sm text-gray-300 hover:text-mango-300 hover:translate-x-1 transition duration-300"
                    aria-label={`Call us at ${farmInfo.contact.phone2}`}
                  >
                    {farmInfo.contact.phone2}
                  </a>
                </div>
              )}
              <div className="flex items-start space-x-2.5">
                <Mail className="h-4 w-4 text-mango-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <a
                  href={`mailto:${farmInfo.contact.email}`}
                  className="inline-block text-[0.8rem] text-gray-300 hover:text-mango-300 hover:translate-x-1 transition duration-300 break-words"
                  aria-label={`Email us at ${farmInfo.contact.email}`}
                >
                  {farmInfo.contact.email.split("@")[0]}
                  <wbr />@{farmInfo.contact.email.split("@")[1]}
                </a>
              </div>
              <div className="flex items-start space-x-2.5">
                <MapPin className="h-4 w-4 text-mango-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span className="text-sm text-gray-300 leading-relaxed">
                  {farmInfo.contact.address}
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="border-t border-white/10 mt-10 pt-6 text-center"
        >
          <p className="text-xs text-gray-400">
            © {currentYear} Golden Harvest Mango Farm. All rights reserved. | Growing nature's sweetest treasures with love and care.
          </p>
        </motion.div>
      </div>
    </footer>
  );
});

// PERFORMANCE OPTIMIZATION ADDED - Display name for better debugging
Footer.displayName = "Footer";

export default Footer;
